import { supabase } from '../config/supabase';
import type { Debt, DebtPayment } from '../types/debt';

export const getAllDebts = async (): Promise<Debt[]> => {
  const { data, error } = await supabase
    .from('debts')
    .select('*')
    .order('updated_at', { ascending: false });

  if (error) {
    console.error('Error fetching debts:', error);
    return [];
  }
  return data.map(mapDebtFromDB);
};

export const getDebtById = async (id: string): Promise<Debt | undefined> => {
  const { data, error } = await supabase
    .from('debts')
    .select('*')
    .eq('id', id)
    .single();
    
  if (error || !data) return undefined;
  return mapDebtFromDB(data);
};

export const createDebt = async (
  debt: Omit<Debt, 'id' | 'createdAt' | 'updatedAt' | 'payments' | 'status' | 'amountPaid'>
): Promise<Debt | null> => {
  const { data: result, error } = await supabase.from('debts').insert({
    creditor_name: debt.creditorName,
    amount: debt.amount,
    amount_paid: 0,
    currency: debt.currency,
    category: debt.category,
    status: 'pending',
    description: debt.description,
    due_date: debt.dueDate || null,
    payments: [],
  }).select().single();

  if (error) {
    console.error('Error creating debt:', error);
    return null;
  }
  return mapDebtFromDB(result);
};

export const updateDebt = async (
  id: string,
  updates: Partial<Omit<Debt, 'id' | 'createdAt' | 'payments'>>
): Promise<Debt | null> => {
  const dbUpdates: any = {};
  if (updates.creditorName !== undefined) dbUpdates.creditor_name = updates.creditorName;
  if (updates.amount !== undefined) dbUpdates.amount = updates.amount;
  if (updates.amountPaid !== undefined) dbUpdates.amount_paid = updates.amountPaid;
  if (updates.currency !== undefined) dbUpdates.currency = updates.currency;
  if (updates.category !== undefined) dbUpdates.category = updates.category;
  if (updates.status !== undefined) dbUpdates.status = updates.status;
  if (updates.description !== undefined) dbUpdates.description = updates.description;
  if (updates.dueDate !== undefined) dbUpdates.due_date = updates.dueDate;
  dbUpdates.updated_at = new Date().toISOString();

  const { data, error } = await supabase.from('debts').update(dbUpdates).eq('id', id).select().single();

  if (error) {
    console.error('Error updating debt:', error);
    return null;
  }
  return mapDebtFromDB(data);
};

export const deleteDebt = async (id: string): Promise<boolean> => {
  const { error } = await supabase.from('debts').delete().eq('id', id);
  if (error) {
    console.error('Error deleting debt:', error);
    return false;
  }
  return true;
};

export const addPayment = async (debtId: string, payment: Omit<DebtPayment, 'id'>): Promise<Debt | null> => {
  const current = await getDebtById(debtId);
  if (!current) return null;

  const newPayment: DebtPayment = {
    ...payment,
    id: Math.random().toString(36).substring(2, 10) + Date.now().toString(36),
  };
  const newAmountPaid = current.amountPaid + payment.amount;
  const newStatus = newAmountPaid >= current.amount ? 'paid' : 'partial';
  const newPayments = [...current.payments, newPayment];

  const dbUpdates = {
    amount_paid: newAmountPaid,
    status: newStatus,
    payments: newPayments,
    updated_at: new Date().toISOString(),
  };

  const { data, error } = await supabase.from('debts').update(dbUpdates).eq('id', debtId).select().single();

  if (error) {
    console.error('Error adding debt payment:', error);
    return null;
  }
  return mapDebtFromDB(data);
};

export interface DebtStats {
  totalDebt: number;
  pendingCount: number;
  overdueCount: number;
  paidThisMonth: number;
}

export const getStats = (debts: Debt[]): DebtStats => {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

  let totalDebt = 0;
  let pendingCount = 0;
  let overdueCount = 0;
  let paidThisMonth = 0;

  for (const d of debts) {
    if (d.status === 'paid') {
      for (const p of d.payments) {
        if (p.date >= startOfMonth) paidThisMonth += p.amount;
      }
      continue;
    }

    const remaining = d.amount - d.amountPaid;
    totalDebt += remaining;

    if (d.status === 'pending' || d.status === 'partial') {
      pendingCount++;
      if (d.dueDate && d.dueDate < now.toISOString().split('T')[0]) overdueCount++;
    }
  }

  return {
    totalDebt,
    pendingCount,
    overdueCount,
    paidThisMonth,
  };
};

const mapDebtFromDB = (row: any): Debt => ({
  id: row.id,
  creditorName: row.creditor_name,
  amount: Number(row.amount),
  amountPaid: Number(row.amount_paid),
  currency: row.currency,
  category: row.category,
  status: row.status,
  description: row.description,
  dueDate: row.due_date,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  payments: Array.isArray(row.payments) ? row.payments : [],
});
